<template>
  <svg
    id="hamburgerMenu"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      id="hamburger"
      ref="hamburger"
      :d="hamburgerPath"
      stroke="#141414"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
</template>

<script>
export default {
  name: 'HamburgerIcon',
  props: {
    lines: {
      type: Number,
      default: 2,
      validator: value => [2, 3].includes(value)
    }
  },
  data() {
    return {
      isCross: false,
      crossPath: 'M17 7L7 17M7 7L17 17',
      animationDuration: 400
    };
  },
  computed: {
    hamburgerPath() {
      return this.lines === 3 ? 'M4 6H20M4 12H20M4 18H20' : 'M4 9H20M4 15H20';
    }
  },
  methods: {
    toCross() {
      if (this.isCross) {
        return;
      }
      this.isCross = true;
      this.animatePath(this.crossPath);
    },

    toHamburger() {
      if (!this.isCross) {
        return;
      }
      this.isCross = false;
      this.animatePath(this.hamburgerPath);
    },

    toggle() {
      if (this.isCross) {
        this.toHamburger();
      } else {
        this.toCross();
      }
    },

    animatePath(targetPath) {
      const path = this.$refs.hamburger;
      const startTime = performance.now();

      const animate = currentTime => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / this.animationDuration, 1);

        // Easing function podobná mina.easeinout
        const easedProgress = this.easeInOut(progress);

        // Interpolace mezi současnou a cílovou cestou
        const interpolatedPath = this.interpolatePath(
          path.getAttribute('d'),
          targetPath,
          easedProgress
        );

        path.setAttribute('d', interpolatedPath);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    },

    easeInOut(time) {
      // Kubická ease-in-out funkce podobná mina.easeinout
      return time < 0.5 ? 4 * time * time * time : 1 - Math.pow(-2 * time + 2, 3) / 2;
    },

    interpolatePath(fromPath, toPath, progress) {
      // Jednoduchá interpolace pro naše specifické cesty
      if (progress === 0) {
        return fromPath;
      }
      if (progress === 1) {
        return toPath;
      }

      const isThreeLines = this.lines === 3;

      // Pro hamburger -> cross přechod
      if (toPath === this.crossPath) {
        if (isThreeLines) {
          // Pro 3 řádky: horní a spodní se stanou křížkem, prostřední zmizí
          const topLineStart = this.interpolatePoint([4, 6], [17, 7], progress);
          const topLineEnd = this.interpolatePoint([20, 6], [7, 17], progress);
          const bottomLineStart = this.interpolatePoint([4, 18], [7, 7], progress);
          const bottomLineEnd = this.interpolatePoint([20, 18], [17, 17], progress);

          // Prostřední čára postupně mizí směrem ke středu
          const middleOpacity = 1 - progress;
          const middleStart = this.interpolatePoint([4, 12], [12, 12], progress);
          const middleEnd = this.interpolatePoint([20, 12], [12, 12], progress);

          if (middleOpacity > 0.1) {
            return `M${topLineStart[0]} ${topLineStart[1]}L${topLineEnd[0]} ${topLineEnd[1]}M${middleStart[0]} ${middleStart[1]}L${middleEnd[0]} ${middleEnd[1]}M${bottomLineStart[0]} ${bottomLineStart[1]}L${bottomLineEnd[0]} ${bottomLineEnd[1]}`;
          }
          return `M${topLineStart[0]} ${topLineStart[1]}L${topLineEnd[0]} ${topLineEnd[1]}M${bottomLineStart[0]} ${bottomLineStart[1]}L${bottomLineEnd[0]} ${bottomLineEnd[1]}`;
        } else {
          // Pro 2 řádky: původní logika
          const topLineStart = this.interpolatePoint([4, 9], [17, 7], progress);
          const topLineEnd = this.interpolatePoint([20, 9], [7, 17], progress);
          const bottomLineStart = this.interpolatePoint([4, 15], [7, 7], progress);
          const bottomLineEnd = this.interpolatePoint([20, 15], [17, 17], progress);

          return `M${topLineStart[0]} ${topLineStart[1]}L${topLineEnd[0]} ${topLineEnd[1]}M${bottomLineStart[0]} ${bottomLineStart[1]}L${bottomLineEnd[0]} ${bottomLineEnd[1]}`;
        }
      } else {
        // Pro cross -> hamburger přechod
        if (isThreeLines) {
          const topLineStart = this.interpolatePoint([17, 7], [4, 6], progress);
          const topLineEnd = this.interpolatePoint([7, 17], [20, 6], progress);
          const bottomLineStart = this.interpolatePoint([7, 7], [4, 18], progress);
          const bottomLineEnd = this.interpolatePoint([17, 17], [20, 18], progress);

          // Prostřední čára se postupně objevuje ze středu
          const middleOpacity = progress;
          const middleStart = this.interpolatePoint([12, 12], [4, 12], progress);
          const middleEnd = this.interpolatePoint([12, 12], [20, 12], progress);

          if (middleOpacity > 0.1) {
            return `M${topLineStart[0]} ${topLineStart[1]}L${topLineEnd[0]} ${topLineEnd[1]}M${middleStart[0]} ${middleStart[1]}L${middleEnd[0]} ${middleEnd[1]}M${bottomLineStart[0]} ${bottomLineStart[1]}L${bottomLineEnd[0]} ${bottomLineEnd[1]}`;
          }
          return `M${topLineStart[0]} ${topLineStart[1]}L${topLineEnd[0]} ${topLineEnd[1]}M${bottomLineStart[0]} ${bottomLineStart[1]}L${bottomLineEnd[0]} ${bottomLineEnd[1]}`;
        } else {
          const topLineStart = this.interpolatePoint([17, 7], [4, 9], progress);
          const topLineEnd = this.interpolatePoint([7, 17], [20, 9], progress);
          const bottomLineStart = this.interpolatePoint([7, 7], [4, 15], progress);
          const bottomLineEnd = this.interpolatePoint([17, 17], [20, 15], progress);

          return `M${topLineStart[0]} ${topLineStart[1]}L${topLineEnd[0]} ${topLineEnd[1]}M${bottomLineStart[0]} ${bottomLineStart[1]}L${bottomLineEnd[0]} ${bottomLineEnd[1]}`;
        }
      }
    },

    interpolatePoint(from, to, progress) {
      return [from[0] + (to[0] - from[0]) * progress, from[1] + (to[1] - from[1]) * progress];
    }
  }
};
</script>
